import {
  isUnlimited,
  type Limit,
  type Plan,
  type PlanFeature,
  planFeatures,
} from "@charisol-spark/billing";

/**
 * Credits-aware plan copy.
 *
 * Lives here — the one module the app's upgrade page and the public pricing
 * site both already import from — rather than in either page, so the two
 * cannot say different things about the same plan.
 *
 * The catalogue itself (`@charisol-spark/billing`) is mid-rename: it is being
 * renamed from `limits.monthlyGenerations` to `limits.monthlyCredits` (at 10x
 * the old figure, since a standard post now costs about 10 credits), and the
 * old field is expected to keep working for a release. This reads either
 * shape so the pricing copy is right before and after that lands, without
 * this package depending on which one shipped.
 */

/** Credits per generation, matching the API's `standardPostCredits`. Used
 * only to derive a figure when the catalogue has not been renamed yet. */
const CREDITS_PER_STANDARD_POST = 10;

/** The catalogue shape once `monthlyGenerations` is renamed to `monthlyCredits`. */
interface LimitsWithCredits {
  monthlyGenerations: Limit;
  monthlyCredits?: Limit;
}

export interface PlanAllowance {
  /** Credits per period; `null` means unlimited. */
  credits: Limit;
  /** How many standard (10-credit) posts that buys; `null` means unlimited. */
  approxPosts: Limit;
}

/** A plan's credit allowance, read from whichever catalogue shape is live. */
export function planAllowance(plan: Plan): PlanAllowance {
  const limits = plan.limits as LimitsWithCredits;
  const credits =
    limits.monthlyCredits ??
    (isUnlimited(limits.monthlyGenerations)
      ? null
      : limits.monthlyGenerations * CREDITS_PER_STANDARD_POST);
  const approxPosts = isUnlimited(credits)
    ? null
    : Math.round(credits / CREDITS_PER_STANDARD_POST);
  return { credits, approxPosts };
}

/** "1,000 credits / month (about 100 posts)" style copy for a plan card. */
export function creditsLabel(plan: Plan): string {
  const { credits, approxPosts } = planAllowance(plan);
  if (isUnlimited(credits)) return "Unlimited credits";
  return `${credits.toLocaleString()} credits / month (about ${approxPosts!.toLocaleString()} posts)`;
}

/** A short cell for a comparison table: "1,000 (~100 posts)" or "Unlimited". */
export function creditsCell(plan: Plan): string {
  const { credits, approxPosts } = planAllowance(plan);
  if (isUnlimited(credits)) return "Unlimited";
  return `${credits.toLocaleString()} (~${approxPosts!.toLocaleString()} posts)`;
}

/**
 * `planFeatures(plan)` with the generations bullet restated in credits.
 *
 * Both pricing surfaces (the app's upgrade page and the public pricing site)
 * render this instead of `planFeatures` directly, so the substitution is
 * made once rather than by two regexes that could drift.
 */
export function planFeaturesWithCredits(plan: Plan): PlanFeature[] {
  return planFeatures(plan).map((f) =>
    /generations/i.test(f.label) ? { ...f, label: creditsLabel(plan) } : f
  );
}
